import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CreditCard, CurrencyBtc, Truck, Package, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Checkout() {
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const hasPhysical = items.some((i) => i.fulfillment !== "digital");
  const allDigital = items.length > 0 && !hasPhysical;

  const [fulfillment, setFulfillment] = useState(allDigital ? "digital" : "mail");
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [address, setAddress] = useState({
    line1: "", city: "", state: "", postal_code: "", country: "US"
  });
  const [loading, setLoading] = useState(false);

  if (items.length === 0) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="checkout-empty">
        <p className="font-mono mb-6">Your cart is empty.</p>
        <Link to="/" className="btn-outline-dark">Back to shop</Link>
      </main>
    );
  }

  // Right now backend creates a single-product session. If multi-item, loop first item.
  // Simplification: checkout first item (most carts single-product for MVP).
  const item = items[0];

  const submit = async () => {
    if (fulfillment === "mail" && (!address.line1 || !address.city || !address.postal_code)) {
      toast.error("Please complete shipping address");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        product_id: item.id,
        quantity: item.qty,
        fulfillment,
        address: fulfillment === "digital" ? null : address,
        origin_url: window.location.origin,
        payment_method: paymentMethod,
      });
      clear();
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <main className="max-w-5xl mx-auto px-6 lg:px-10 py-16" data-testid="checkout-page">
      <p className="overline opacity-60 mb-2">// Checkout</p>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-10">Complete your order</h1>

      <div className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 space-y-10">
          {/* Fulfillment */}
          <section data-testid="fulfillment-section">
            <p className="overline mb-4">01 / Fulfillment</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { v: "mail", icon: <Package size={20}/>, label: "Mail order", sub: "Ship to any address" },
                { v: "delivery", icon: <Truck size={20}/>, label: "Local delivery", sub: "Hand-delivered locally" },
                ...(allDigital || item.fulfillment === "digital" ? [{ v: "digital", icon: <DownloadSimple size={20}/>, label: "Digital", sub: "Instant download" }] : []),
              ].map((o) => (
                <button key={o.v} onClick={() => setFulfillment(o.v)}
                  className={`text-left p-5 border transition-all ${fulfillment === o.v
                    ? "border-signal bg-[#111111] shadow-[0_0_18px_rgba(255,7,58,0.45)]" : "border-white/15 bg-[#111111] hover:border-signal"}`}
                  data-testid={`fulfillment-${o.v}`}>
                  {o.icon}
                  <p className="font-display font-bold mt-2">{o.label}</p>
                  <p className="font-mono text-xs opacity-60 mt-1">{o.sub}</p>
                </button>
              ))}
            </div>
          </section>

          {/* Address */}
          {fulfillment !== "digital" && (
            <section data-testid="address-section">
              <p className="overline mb-4">02 / {fulfillment === "mail" ? "Shipping address" : "Delivery address"}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className="input-sharp sm:col-span-2" placeholder="Street address"
                       value={address.line1} onChange={(e) => setAddress({...address, line1: e.target.value})}
                       data-testid="address-line1" />
                <input className="input-sharp" placeholder="City"
                       value={address.city} onChange={(e) => setAddress({...address, city: e.target.value})}
                       data-testid="address-city" />
                <input className="input-sharp" placeholder="State / Region"
                       value={address.state} onChange={(e) => setAddress({...address, state: e.target.value})}
                       data-testid="address-state" />
                <input className="input-sharp" placeholder="Postal code"
                       value={address.postal_code} onChange={(e) => setAddress({...address, postal_code: e.target.value})}
                       data-testid="address-postal" />
                <input className="input-sharp" placeholder="Country"
                       value={address.country} onChange={(e) => setAddress({...address, country: e.target.value})}
                       data-testid="address-country" />
              </div>
            </section>
          )}

          {/* Payment */}
          <section data-testid="payment-section">
            <p className="overline mb-4">0{fulfillment === "digital" ? "2" : "3"} / Payment</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <button onClick={() => setPaymentMethod("card")}
                className={`text-left p-5 border transition-all ${paymentMethod === "card"
                  ? "border-signal bg-[#111111] shadow-[0_0_18px_rgba(255,7,58,0.45)]" : "border-white/15 bg-[#111111] hover:border-signal"}`}
                data-testid="pay-card">
                <CreditCard size={20}/>
                <p className="font-display font-bold mt-2">Card</p>
                <p className="font-mono text-xs opacity-60 mt-1">Visa · Mastercard · Amex</p>
              </button>
              <button onClick={() => setPaymentMethod("crypto")}
                className={`text-left p-5 border transition-all ${paymentMethod === "crypto"
                  ? "border-signal bg-[#111111] shadow-[0_0_18px_rgba(255,7,58,0.45)]" : "border-white/15 bg-[#111111] hover:border-signal"}`}
                data-testid="pay-crypto">
                <CurrencyBtc size={20}/>
                <p className="font-display font-bold mt-2">Crypto</p>
                <p className="font-mono text-xs opacity-60 mt-1">BTC · ETH · USDC via Stripe</p>
              </button>
            </div>
          </section>
        </div>

        {/* Summary */}
        <aside className="lg:col-span-2 border border-white/10 bg-[#111111] p-6 h-fit sticky top-24"
               data-testid="checkout-summary">
          <p className="overline opacity-60 mb-4">Summary</p>
          <div className="flex items-center gap-4 mb-4 pb-4 border-b border-white/10">
            <img src={item.image_url} alt={item.name} className="w-16 h-16 object-cover border border-white/10"/>
            <div className="flex-1">
              <p className="font-display font-bold">{item.name}</p>
              <p className="font-mono text-xs opacity-60">Qty {item.qty}</p>
            </div>
            <p className="font-mono text-sm">${(item.price * item.qty).toFixed(2)}</p>
          </div>
          <div className="flex justify-between font-mono text-sm mb-2">
            <span>Subtotal</span><span>${total.toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-mono text-sm mb-6 opacity-60">
            <span>Taxes</span><span>Calculated at Stripe</span>
          </div>
          <button onClick={submit} disabled={loading}
            className="btn-signal w-full justify-center" data-testid="pay-submit-btn">
            {loading ? "Redirecting…" : `Pay $${total.toFixed(2)} →`}
          </button>
          <p className="font-mono text-[10px] opacity-50 mt-4 leading-relaxed">
            You will be redirected to Stripe to complete the payment securely.
          </p>
        </aside>
      </div>
      {items.length > 1 && (
        <p className="font-mono text-xs opacity-60 mt-6">
          Note: multi-item checkout coming soon. For now the top item in your cart is checked out.
        </p>
      )}
    </main>
  );
}
